import { useRef, useState } from 'react';

import { message } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory } from 'react-router-dom';
import { T } from 'react-translator-component';

import {
  setIsUserAuthenticated,
  setRoutes,
} from '../../../../redux/actions/user';
import { staticRoutes } from '../../../../routers/Routes';
import { createAuthentication } from '../../../../services/authentication';
import { isMonetizationProUser } from '../../../../utils/isMonetizationProUser/isMonetizationProUser';
import { isIntelbrasTenant } from '../../../../utils/isIntelbrasTenant/isIntelbrasTenant';
import { isProUser } from '../../../../utils/isProUser/isProUser';
import { isTerpelTenant } from '../../../../utils/isTerpelTenant/isTerpelTenant';
import { isVoucherProUser } from '../../../../utils/isVoucherProUser/isVoucherProUser';
import { ensureLoginFromEnv } from '../../../../services/licenseService';

export const useLogin = ({ dispatch }) => {
  const history = useHistory();

  const captchaRef = useRef<ReCAPTCHA>(null);

  const [recaptchaResponse, setRecaptchaResponse] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const [twoFactorData, setTwoFactorData] = useState<{ email: string; maskedEmail: string } | null>(null);

  const onLoginSuccess = (user) => {
    dispatch(setIsUserAuthenticated(true));

    dispatch(setIsUserAuthenticated(true));

    let mappedRoutes = user?.permissions?.map(({ route }) => {
      const mappedStaticRoute = staticRoutes.find(({ path }) => path === route);

      return mappedStaticRoute || false;
    });

    if (!isMonetizationProUser() && !isTerpelTenant()) {
      mappedRoutes = mappedRoutes?.filter(
        (route) =>
          !route?.path?.startsWith('/dynamic-pricing') &&
          !route?.path?.startsWith('/virtual-group') &&
          !route?.path?.startsWith('/financial'),
      );
    }

    if (!isVoucherProUser() && !isTerpelTenant()) {
      mappedRoutes = mappedRoutes?.filter(
        (route) => !route?.path?.startsWith('/voucher'),
      );
    }

    if (!isProUser()) {
      mappedRoutes = mappedRoutes?.filter(
        (route) => !route?.path?.startsWith('/log'),
      );
    }

    dispatch(setRoutes(mappedRoutes));

    setIsLoginLoading(false);

    history.push('/');

    message.success(T('Login successfully!'));
  };

  const onLoginFailed = (status) => {
    dispatch(setIsUserAuthenticated(false));

    setIsLoginLoading(false);

    switch (status) {
      case 401:
        message.error(T('Unable to log in. Please check if the email or password is correct.'));
        break;
      case 403:
        message.error(T('Account temporarily blocked. Try again in 15 minutes.'));
        break;
      default:
        message.error(T('Error when trying to Login.'));
        break;
    }
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    event.persist();

    const userForm = {
      email: event.target.email.value,
      password: event.target.password.value,
      recaptchaResponse: recaptchaResponse,
    };

    setIsLoginLoading(true);

    (async () => {
      const userData = await createAuthentication(userForm);
      const { type, status, data } = userData;

      if (type === 'success' && data?.twoFactorRequired) {
        setTwoFactorData({ email: userForm.email, maskedEmail: data.maskedEmail || '' });
        setIsLoginLoading(false);
        return;
      }

      if (type === 'success' && data?.token !== null && data?.user?.permissions.length) {
        onLoginSuccess(data.user);

        if (isIntelbrasTenant()) {
          await ensureLoginFromEnv();
        }
      } else {
        onLoginFailed(status);

        captchaRef.current.reset();
      }
    })();
  };

  const handleTwoFactorSuccess = (user) => {
    onLoginSuccess(user);
  };

  const handleBackToLogin = () => {
    setTwoFactorData(null);
    setIsLoginLoading(false);
    captchaRef.current?.reset();
    setIsVerified(false);
    setRecaptchaResponse('');
  };

  const recaptchaLoaded = (response) => {
    if (response) {
      setRecaptchaResponse(response);

      setIsVerified(true);
    }
  };

  const recaptchaExpired = () => {
    setIsVerified(false);
  };

  return {
    captchaRef,
    handleFormSubmit,
    isLoginLoading,
    isVerified,
    recaptchaExpired,
    recaptchaLoaded,
    twoFactorData,
    handleTwoFactorSuccess,
    handleBackToLogin,
  };
};
